import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Form, Table } from 'react-bootstrap';
import axios from 'axios';

const qty = (v) => (v == null ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 }));

/**
 * Materials at or below their reorder point (set on Org Admin > Materials), counting what sale orders have reserved
 * and what is already on order. Tick the ones to buy, adjust the quantities, and make one purchase order for them:
 * the order form opens with each item, its quantity, and the vendor and rate it was last bought at.
 */
function LowStock({ active, onMakePO, reloadKey }) {
  const [rows, setRows] = useState(null);
  const [chosen, setChosen] = useState({});
  const [open, setOpen] = useState(true);

  const load = useCallback(() => {
    axios.get('/api/inventory/low-stock')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setRows(list);
        setChosen(Object.fromEntries(list.map((x) => [x.itemId, String(Number(x.suggestedQuantity))])));
      })
      .catch(() => setRows([]));
  }, []);

  useEffect(() => { if (active) load(); }, [active, load, reloadKey]);

  if (!active || !rows || rows.length === 0) return null;

  const picked = rows.filter((r) => chosen[r.itemId] !== undefined && Number(chosen[r.itemId]) > 0);
  const toggle = (r) => setChosen((c) => {
    const next = { ...c };
    if (next[r.itemId] !== undefined) delete next[r.itemId]; else next[r.itemId] = String(Number(r.suggestedQuantity));
    return next;
  });

  return (
    <Card className="mb-3 border-warning">
      <Card.Header className="py-2 d-flex flex-wrap align-items-center gap-2">
        <strong><i className="bi bi-exclamation-triangle text-warning me-1" />Running low ({rows.length})</strong>
        <span className="small text-muted">At or below the reorder point, counting what&apos;s reserved and on order</span>
        <Button size="sm" variant="link" className="ms-auto p-0" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'}</Button>
      </Card.Header>
      {open && (
        <Card.Body className="p-0">
          <Table size="sm" responsive className="small mb-0">
            <thead>
              <tr>
                <th style={{ width: '2rem' }} />
                <th>Material</th>
                <th className="text-end">In stock</th>
                <th className="text-end">On order</th>
                <th className="text-end">Reorder at</th>
                <th style={{ width: '9rem' }}>Order</th>
                <th>Last bought from</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const on = chosen[r.itemId] !== undefined;
                const free = Number(r.onHand) - Number(r.reserved);
                return (
                  <tr key={r.itemId}>
                    <td><Form.Check type="checkbox" checked={on} onChange={() => toggle(r)} aria-label={`Order ${r.itemName}`} /></td>
                    <td>
                      {r.itemName}
                      {r.outOfStock && <Badge bg="danger" className="ms-2">Out of stock</Badge>}
                    </td>
                    <td className="text-end text-nowrap">
                      {qty(free)} {r.uom || ''}
                      {Number(r.reserved) > 0 && <div className="text-muted">{qty(r.reserved)} reserved</div>}
                    </td>
                    <td className="text-end text-nowrap">{Number(r.onOrder) > 0 ? `${qty(r.onOrder)} ${r.uom || ''}` : '-'}</td>
                    <td className="text-end text-nowrap">{qty(r.reorderPoint)}</td>
                    <td>
                      <Form.Control size="sm" type="number" min="0" step="any" disabled={!on} value={on ? chosen[r.itemId] : ''}
                        onChange={(e) => setChosen((c) => ({ ...c, [r.itemId]: e.target.value }))} />
                    </td>
                    <td>
                      {r.vendorName ? <>{r.vendorName}{r.lastPrice != null && <span className="text-muted"> @ {Number(r.lastPrice)}</span>}</> : <span className="text-muted">Not bought yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <div className="d-flex flex-wrap align-items-center gap-2 p-2 border-top">
            <span className="small text-muted me-auto">
              Order quantity: the preferred order quantity, or enough to get back to the reorder point plus safety stock; never below the minimum order.
            </span>
            <Button size="sm" disabled={picked.length === 0}
              onClick={() => onMakePO(picked.map((r) => ({ ...r, orderQuantity: Number(chosen[r.itemId]) })))}>
              Make purchase order ({picked.length})
            </Button>
          </div>
        </Card.Body>
      )}
    </Card>
  );
}

export default LowStock;
